import { useNavigate, useParams } from 'react-router-dom'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import ScoreRadar from '@/components/dashboard/ScoreRadar'
import { useLang } from '@/hooks/useLang'
import { useAudio } from '@/hooks/useAudio'
import { MOCK_DESIGNS } from '@/data/mockDesigns'

const STATUS_LABEL: Record<string, { fr: string; en: string; color: string }> = {
  draft: { fr: 'Brouillon', en: 'Draft', color: 'text-on-surface-variant' },
  completed: { fr: 'Finalisé', en: 'Completed', color: 'text-primary-container' },
  exported: { fr: 'Exporté', en: 'Exported', color: 'text-secondary' },
}

const AI_LABELS: Record<number, { fr: string; en: string }> = {
  1: { fr: 'Téléopéré', en: 'Teleoperated' },
  2: { fr: 'Assisté', en: 'Assisted' },
  3: { fr: 'Supervisé', en: 'Supervised' },
  4: { fr: 'Autonome', en: 'Autonomous' },
  5: { fr: 'Pleinement autonome', en: 'Fully autonomous' },
}

const BAND_LABELS: Record<string, { fr: string; en: string }> = {
  companion: { fr: 'Companion', en: 'Companion' },
  assistant_pro: { fr: 'Assistant Pro', en: 'Assistant Pro' },
  operator_specialist: { fr: 'Operator Specialist', en: 'Operator Specialist' },
}

const COPY = {
  fr: {
    back: 'Retour au tableau de bord',
    editBtn: 'Modifier',
    exportBtn: 'Exporter PDF',
    deleteBtn: 'Supprimer',
    scoreSection: 'Scores de conception',
    feasibility: 'Faisabilité',
    complexity: 'Complexité',
    market: 'Demande marché',
    innovation: 'Innovation',
    summarySection: 'Résumé de configuration',
    role: 'Rôle',
    environment: 'Environnement',
    aiLevel: 'Niveau IA',
    missions: 'Missions',
    budget: 'Budget',
    budgetRange: 'Fourchette',
    created: 'Créé le',
    updated: 'Modifié le',
    notFound: 'Configuration introuvable.',
  },
  en: {
    back: 'Back to dashboard',
    editBtn: 'Edit',
    exportBtn: 'Export PDF',
    deleteBtn: 'Delete',
    scoreSection: 'Design scores',
    feasibility: 'Feasibility',
    complexity: 'Complexity',
    market: 'Market demand',
    innovation: 'Innovation',
    summarySection: 'Configuration summary',
    role: 'Role',
    environment: 'Environment',
    aiLevel: 'AI level',
    missions: 'Missions',
    budget: 'Budget',
    budgetRange: 'Range',
    created: 'Created',
    updated: 'Updated',
    notFound: 'Configuration not found.',
  },
}

function formatDate(iso: string, lang: 'fr' | 'en'): string {
  return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
  })
}

export default function DesignPage() {
  const navigate = useNavigate()
  const { designId } = useParams<{ designId: string }>()
  const { lang, toggle: toggleLang } = useLang()
  const { enabled: audioEnabled, playing: audioPlaying, toggle: toggleAudio } = useAudio(lang)
  const t = COPY[lang]

  const design = MOCK_DESIGNS.find((d) => d.id === designId)

  return (
    <div className="min-h-screen bg-surface text-on-surface overflow-x-hidden selection:bg-primary-container selection:text-on-primary-container">
      <Header
        lang={lang}
        audioEnabled={audioEnabled}
        audioPlaying={audioPlaying}
        onToggleAudio={toggleAudio}
        onToggleLang={toggleLang}
      />

      <main className="relative pt-28 pb-24 px-6 md:px-12 max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <button
          onClick={() => void navigate('/dashboard')}
          className="flex items-center gap-2 text-xs font-label tracking-widest uppercase text-on-surface-variant hover:text-primary transition-colors mb-10"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          {t.back}
        </button>

        {!design ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20">search_off</span>
            <p className="font-headline text-xl text-on-surface-variant">{t.notFound}</p>
          </div>
        ) : (
          <>
            {/* Page header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className={`label-technical ${STATUS_LABEL[design.status]?.color}`}>
                    {STATUS_LABEL[design.status]?.[lang]}
                  </span>
                  <span className="text-outline-variant/40">·</span>
                  <span className="label-technical">{design.id}</span>
                </div>
                <h1 className="font-headline text-4xl font-bold text-on-surface">{design.name}</h1>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => void navigate('/configurator')}
                  className="btn-secondary text-sm py-3 px-6"
                >
                  <span className="material-symbols-outlined text-base">edit</span>
                  {t.editBtn}
                </button>
                <button className="btn-primary text-sm py-3 px-6">
                  <span className="material-symbols-outlined text-base">picture_as_pdf</span>
                  {t.exportBtn}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left column — scores */}
              <div className="lg:col-span-1 flex flex-col gap-6">
                <div className="glass-panel rounded-xl border border-outline-variant/10 p-8">
                  <h2 className="font-headline text-lg font-bold text-on-surface mb-6">{t.scoreSection}</h2>
                  <ScoreRadar scores={design.scores} lang={lang} />
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {[
                      { key: 'feasibility', label: t.feasibility, value: design.scores.feasibility, color: 'text-primary-container' },
                      { key: 'complexity', label: t.complexity, value: design.scores.complexity, color: 'text-secondary' },
                      { key: 'market', label: t.market, value: design.scores.market_demand, color: 'text-primary' },
                      { key: 'innovation', label: t.innovation, value: design.scores.innovation, color: 'text-tertiary' },
                    ].map((s) => (
                      <div key={s.key} className="bg-surface-container rounded-lg p-3">
                        <div className="label-technical mb-1">{s.label}</div>
                        <div className={`font-headline text-xl font-bold ${s.color}`}>{s.value}%</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dates */}
                <div className="glass-panel rounded-xl border border-outline-variant/10 p-6 space-y-3">
                  <div className="flex justify-between text-xs font-label">
                    <span className="text-on-surface-variant">{t.created}</span>
                    <span className="text-on-surface">{formatDate(design.created_at, lang)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-label">
                    <span className="text-on-surface-variant">{t.updated}</span>
                    <span className="text-on-surface">{formatDate(design.updated_at, lang)}</span>
                  </div>
                </div>
              </div>

              {/* Right column — config summary */}
              <div className="lg:col-span-2">
                <div className="glass-panel rounded-xl border border-outline-variant/10 p-8">
                  <h2 className="font-headline text-lg font-bold text-on-surface mb-6">{t.summarySection}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: t.role, value: design.role.replace(/_/g, ' '), icon: 'smart_toy' },
                      { label: t.environment, value: design.environment.replace(/_/g, ' '), icon: 'location_on' },
                      { label: t.aiLevel, value: AI_LABELS[design.ai_level]?.[lang] ?? `L${design.ai_level}`, icon: 'psychology' },
                      { label: t.missions, value: String(design.missions_count), icon: 'task_alt' },
                      { label: t.budget, value: BAND_LABELS[design.budget_band]?.[lang] ?? design.budget_band, icon: 'payments' },
                      { label: t.budgetRange, value: design.budget_range, icon: 'euro' },
                    ].map((row) => (
                      <div key={row.label} className="bg-surface-container rounded-xl p-5 flex items-start gap-3">
                        <span className="material-symbols-outlined text-primary/60 text-xl mt-0.5">{row.icon}</span>
                        <div>
                          <div className="label-technical mb-1">{row.label}</div>
                          <div className="font-headline text-base font-bold text-on-surface capitalize">{row.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Danger zone */}
                <div className="mt-6 flex justify-end">
                  <button className="text-xs font-label tracking-widest uppercase text-on-surface-variant/40 hover:text-error transition-colors flex items-center gap-1 px-4 py-2">
                    <span className="material-symbols-outlined text-sm">delete</span>
                    {t.deleteBtn}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer lang={lang} />
    </div>
  )
}
